// Peer-connection liveness + transport intelligence — the model behind the SDK's 3–5 s
// keep-alive. Pure and framework-agnostic so it is unit-testable in Node and shared by
// every platform. The engine (zr-webrtc) samples each RTCPeerConnection on the keep-alive
// tick and folds the result through these helpers into one real-time record per peer.
//
// WebRTC already keeps the active ICE path warm: once a candidate pair is selected, the
// agent sends STUN binding (consent-freshness, RFC 7675) requests every ~5 s over that
// transport — which is the TURN/TCP relay when the direct path is blocked. The keep-alive
// here SAMPLES that live path (via getStats) on a 3–5 s cadence to (a) maintain an
// accurate per-peer connection state machine, (b) surface whether the path is relayed
// over TURN and whether it is TCP/TLS vs UDP, and (c) layer transport quality so the
// telemetry HUD reflects reality in real time.

import type { IceConnectionState } from './callState';
import type { QualityLevel, QualityDetails } from './index';

/** Transport protocol of the active local ICE candidate. */
export type TransportProtocol = 'udp' | 'tcp' | 'tls' | 'unknown';

/** ICE candidate kind on the selected pair. */
export type CandidateKind = 'host' | 'srflx' | 'prflx' | 'relay' | 'unknown';

/** The live network path a peer connection is actually using. */
export interface PeerTransport {
  localCandidate: CandidateKind;
  remoteCandidate: CandidateKind;
  /** Protocol of the local candidate (udp/tcp/tls). */
  protocol: TransportProtocol;
  /** When relayed via TURN, the protocol to the TURN server ('udp' | 'tcp' | 'tls'). */
  relayProtocol?: string;
  /** True when the media path traverses a TURN relay (CoTurn). */
  usingTurn: boolean;
  /** True when the active path is TCP/TLS rather than UDP. */
  usingTcp: boolean;
}

/** The keep-alive default and the bounds the brief specifies (3–5 s). */
export const KEEPALIVE_DEFAULT_MS = 4000 as const;
export const KEEPALIVE_MIN_MS = 3000 as const;
export const KEEPALIVE_MAX_MS = 5000 as const;

/** Clamp a requested keep-alive interval into the supported 3–5 s window. */
export function clampKeepAlive(ms: number | undefined): number {
  if (!ms || Number.isNaN(ms)) return KEEPALIVE_DEFAULT_MS;
  return Math.min(KEEPALIVE_MAX_MS, Math.max(KEEPALIVE_MIN_MS, Math.round(ms)));
}

function candidateKind(t: unknown): CandidateKind {
  switch (t) {
    case 'host':
    case 'srflx':
    case 'prflx':
    case 'relay':
      return t;
    default:
      return 'unknown';
  }
}

function protocolOf(p: unknown): TransportProtocol {
  const s = String(p ?? '').toLowerCase();
  if (s === 'udp') return 'udp';
  if (s === 'tcp') return 'tcp';
  if (s === 'tls' || s === 'ssltcp') return 'tls';
  return 'unknown';
}

/**
 * Derive the active {@link PeerTransport} from a getStats report (anything with a
 * LiveKit/WebRTC-style `forEach`). Returns undefined when no candidate pair is selected
 * yet. Pure: it only reads the stats snapshot.
 */
export function extractPeerTransport(stats: { forEach(cb: (report: any) => void): void }): PeerTransport | undefined {
  let pair: any;
  const byId = new Map<string, any>();
  stats.forEach((r: any) => {
    if (r && r.id) byId.set(r.id, r);
    if (r && r.type === 'candidate-pair' && (r.nominated || r.state === 'succeeded') && !pair) pair = r;
  });
  // Prefer an explicitly nominated/succeeded pair; fall back to any candidate-pair.
  if (!pair) {
    stats.forEach((r: any) => {
      if (!pair && r && r.type === 'candidate-pair') pair = r;
    });
  }
  if (!pair) return undefined;

  const local = byId.get(pair.localCandidateId) || {};
  const remote = byId.get(pair.remoteCandidateId) || {};
  const localKind = candidateKind(local.candidateType);
  const remoteKind = candidateKind(remote.candidateType);
  const protocol = protocolOf(local.protocol);
  const relayProtocol = localKind === 'relay' ? String(local.relayProtocol ?? '').toLowerCase() || undefined : undefined;

  return {
    localCandidate: localKind,
    remoteCandidate: remoteKind,
    protocol,
    relayProtocol,
    usingTurn: localKind === 'relay' || remoteKind === 'relay',
    usingTcp: protocol === 'tcp' || protocol === 'tls' || relayProtocol === 'tcp' || relayProtocol === 'tls',
  };
}

// ---------------------------------------------------------------------------
// Live telemetry — accurate, WINDOWED network quality from getStats counters.
// Pure so the P2P and SFU transports share one correct implementation and it is
// unit-testable without a browser.
// ---------------------------------------------------------------------------

/** Raw counters sampled from one getStats() snapshot at time `ts`. */
export interface StatsSample {
  ts: number;
  bytesRecv: number;       // cumulative inbound RTP bytes
  bytesSent: number;       // cumulative outbound RTP bytes
  packetsLost: number;     // cumulative lost packets
  packetsReceived: number; // cumulative received packets
  rtt: number;             // current round-trip time, ms (instantaneous from candidate-pair)
  jitter: number;          // ms (worst of audio/video)
  availableOutgoingKbps: number; // BWE from the candidate pair
}

/**
 * Parse a getStats report (anything with `forEach`) into a {@link StatsSample}. RTT, jitter
 * and BWE are instantaneous; byte/packet counters are cumulative and meant to be differenced
 * against the previous sample (see {@link computeQualityDetails}). Pure — reads only the snapshot.
 */
export function extractStatsSample(stats: { forEach(cb: (r: any) => void): void }, now: number): StatsSample {
  let rtt = 0, jitter = 0, availableOutgoingKbps = 0;
  let bytesRecv = 0, bytesSent = 0, packetsLost = 0, packetsReceived = 0;
  stats.forEach((report: any) => {
    if (report.type === 'candidate-pair' && (report.state === 'succeeded' || report.nominated)) {
      if (typeof report.currentRoundTripTime === 'number') rtt = report.currentRoundTripTime * 1000;
      if (report.availableOutgoingBitrate) availableOutgoingKbps = report.availableOutgoingBitrate / 1000;
    }
    const kind = report.kind || report.mediaType;
    if (report.type === 'inbound-rtp' && (kind === 'video' || kind === 'audio')) {
      packetsLost += report.packetsLost || 0;
      packetsReceived += report.packetsReceived || 0;
      jitter = Math.max(jitter, (report.jitter || 0) * 1000);
      bytesRecv += report.bytesReceived || 0;
    }
    if (report.type === 'outbound-rtp' && (kind === 'video' || kind === 'audio')) {
      bytesSent += report.bytesSent || 0;
    }
  });
  return { ts: now, bytesRecv, bytesSent, packetsLost, packetsReceived, rtt, jitter, availableOutgoingKbps };
}

/**
 * Fold a current + previous {@link StatsSample} into {@link QualityDetails}. Critically, loss
 * and throughput are WINDOWED — computed from the delta since the previous sample — so the HUD
 * reflects the network RIGHT NOW (a current loss burst shows immediately) rather than a
 * cumulative average that a long healthy session would mask. Falls back to cumulative loss and
 * zero throughput on the first sample. Returns `bytesDelta` too (for the liveness check).
 */
export function computeQualityDetails(curr: StatsSample, prev?: StatsSample): { details: QualityDetails; bytesDelta: number } {
  let downKbps = 0, upKbps = 0, bytesDelta = 0;
  let lossRate: number;

  if (prev) {
    const dt = (curr.ts - prev.ts) / 1000;
    if (dt > 0) {
      downKbps = Math.max(0, ((curr.bytesRecv - prev.bytesRecv) * 8) / 1000 / dt);
      upKbps = Math.max(0, ((curr.bytesSent - prev.bytesSent) * 8) / 1000 / dt);
    }
    bytesDelta = Math.max(0, curr.bytesRecv - prev.bytesRecv) + Math.max(0, curr.bytesSent - prev.bytesSent);
    // Windowed loss: lost vs received SINCE the last sample. Counters can reset on an ICE
    // restart (curr < prev) — clamp negatives to 0 so a reset reads as "no recent loss".
    const lostDelta = Math.max(0, curr.packetsLost - prev.packetsLost);
    const recvDelta = Math.max(0, curr.packetsReceived - prev.packetsReceived);
    const windowTotal = lostDelta + recvDelta;
    lossRate = windowTotal > 0 ? (lostDelta / windowTotal) * 100 : 0;
  } else {
    const total = curr.packetsLost + curr.packetsReceived;
    lossRate = total > 0 ? (curr.packetsLost / total) * 100 : 0;
  }

  // Light 2-sample smoothing on RTT + jitter: the raw candidate-pair RTT is spiky (it swings
  // 300↔650ms tick-to-tick on a relay), which would flap the reported quality. Blending with
  // the previous sample reports a stable "typical" figure without hiding a real trend.
  const rtt = prev ? 0.5 * prev.rtt + 0.5 * curr.rtt : curr.rtt;
  const jitter = prev ? 0.5 * prev.jitter + 0.5 * curr.jitter : curr.jitter;

  const details: QualityDetails = {
    rtt: Math.round(rtt),
    lossRate: Math.round(Math.min(100, lossRate) * 10) / 10,
    jitter: Math.round(jitter * 10) / 10,
    downKbps: Math.round(downKbps),
    upKbps: Math.round(upKbps),
    availableOutgoingKbps: Math.round(curr.availableOutgoingKbps),
  };
  return { details, bytesDelta };
}

/**
 * Debounces the quality LEVEL so it doesn't flap on single-tick RTT/jitter spikes. A changed
 * reading must persist for `confirmTicks` samples before it commits — unless it's a big jump
 * (≥2 levels, e.g. the connection genuinely dying), which commits immediately. Fast to reflect
 * a real change, slow to chase transient noise. One instance per peer; pure + testable.
 */
export class QualitySmoother {
  private current: number;
  private pending: number;
  private pendingCount = 0;
  constructor(private confirmTicks = 2, initialScore = 4) {
    this.current = initialScore;
    this.pending = initialScore;
  }
  /** Feed the raw classified score (1-4); returns the smoothed score to display/emit. */
  update(rawScore: number): number {
    if (rawScore === this.current) {
      this.pendingCount = 0;
      this.pending = rawScore;
      return this.current;
    }
    if (rawScore !== this.pending) {
      this.pending = rawScore;
      this.pendingCount = 1;
    } else {
      this.pendingCount++;
    }
    if (Math.abs(rawScore - this.current) >= 2 || this.pendingCount >= this.confirmTicks) {
      this.current = rawScore;
      this.pendingCount = 0;
    }
    return this.current;
  }
  get score(): number {
    return this.current;
  }
}

/** The label for a 1-4 quality score (shared so engine + UI agree). */
export function qualityLevelForScore(score: number): QualityLevel {
  return score <= 1 ? 'poor' : score === 2 ? 'fair' : score === 3 ? 'good' : 'excellent';
}

/** A single keep-alive sample's liveness inputs. */
export interface LivenessSample {
  /** Total bytes (recv+sent) moved since the previous sample. */
  bytesDelta: number;
  iceConnectionState: IceConnectionState;
}

/**
 * Is the peer connection alive on this sample? Dead/recovering ICE states are not alive;
 * otherwise the path counts as alive when ICE is connected/completed or bytes are moving
 * (so a muted-but-connected peer is still "alive", while a silent disconnected peer is not).
 */
export function isAlive(s: LivenessSample): boolean {
  if (s.iceConnectionState === 'failed' || s.iceConnectionState === 'closed' || s.iceConnectionState === 'disconnected') {
    return false;
  }
  return s.iceConnectionState === 'connected' || s.iceConnectionState === 'completed' || s.bytesDelta > 0;
}

/**
 * The consolidated, real-time record the SDK maintains per participant and emits on every
 * keep-alive tick as a `peer-heartbeat` event — the single source of truth that layers
 * connection state + live transport (TURN/TCP visibility) + quality together.
 */
export interface PeerHeartbeat {
  participantId: string;
  iceConnectionState: IceConnectionState;
  /** RTCPeerConnectionState (`connectionState`). */
  connectionState: string;
  transport?: PeerTransport;
  quality: { score: number; level: QualityLevel; details: QualityDetails };
  alive: boolean;
  /** Consecutive keep-alive ticks this peer has been not-alive (drives reconnect/teardown). */
  staleTicks: number;
  ts: number;
}
